import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  SimpleChanges,
  inject,
} from '@angular/core';

@Directive({
  selector: '[stickyPosition]',
  standalone: true,
})
export class StickyPositionDirective {
  renderer = inject(Renderer2);
  cdr = inject(ChangeDetectorRef);
  el = inject(ElementRef);

  @Input() stickyPosition: any;
  @Input() loading: any;
  @Input() stickyItemClasses: string =
    'dark:!tw-bg-dark-500 !tw-bg-white tw-z-[1]';
  @Input() stickyCondition: boolean;
  @Input() applyStickyPosition: boolean;
  @Input() pageName: string;
  @HostListener('window:resize') onResize() {
    this.calculateAndSetStickyPosition();
  }

  ngAfterContentInit(): void {
    let timeOut = setTimeout(() => {
      this.checkAndApplySticky();
      clearTimeout(timeOut);
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading) {
      let timeOut = setTimeout(() => {
        this.checkAndApplySticky();
        clearTimeout(timeOut);
      }, 100);
    }
    if (changes.stickyCondition) this.checkAndApplySticky();
  }

  checkAndApplySticky() {
    if (this.stickyCondition) this.calculateAndSetStickyPosition();
    else this.removeStickyStyles();
  }

  calculateAndSetStickyPosition() {
    if (!this.applyStickyPosition) return;
    const stickyItems = this.el.nativeElement.querySelectorAll(
      `${this.pageName} .sticky-item`
    );

    if (stickyItems.length) {
      stickyItems.forEach((item: HTMLElement, index: number) => {
        if (!item.classList.contains('sticky-item')) return;
        const position = item.getAttribute('position');

        if (position == 'left') item.setAttribute('order', '99');

        //* Determine the sibling based on the position ('left' or 'right')
        let nextOrPreviousSibling =
          position == 'left'
            ? item.nextElementSibling
            : item.previousElementSibling;

        let elementsWidth: number[] = [];

        this.addClasses(item, position);
        let lastElement: HTMLElement | null = null;

        //* Loop through siblings and calculate their widths
        while (nextOrPreviousSibling) {
          const sibling = nextOrPreviousSibling as HTMLElement;
          lastElement = sibling;

          elementsWidth.push(sibling.offsetWidth);
          nextOrPreviousSibling =
            position == 'left'
              ? nextOrPreviousSibling.nextElementSibling
              : nextOrPreviousSibling.previousElementSibling;
        }

        //* Calculate the total width of all siblings
        const totalWidth = elementsWidth.length
          ? elementsWidth.reduce((sum, width) => sum + width, 0) - 2
          : -1;

        this.renderer.setStyle(
          item,
          position == 'left' ? 'left' : 'right',
          `${totalWidth}px`
        );

        if (position == 'left') item?.setAttribute('order', '99');
        this.renderer.addClass(item, 'lg:!tw-sticky');
        this.removeStickyStylesOnSmallScreens(stickyItems);
      });
    }
  }

  addClasses(item: HTMLElement, position: string) {
    if (!this.stickyItemClasses || window.innerWidth < 1024) return;

    const baseClasses =
      item.tagName === 'TH'
        ? 'lg:!tw-bg-[#f2f6f7] lg:dark:!tw-bg-[#29293d] tw-z-[1]'
        : 'tw-z-[1]';

    const borderClass =
      position === 'left'
        ? item.tagName === 'TH'
          ? 'dashed-border-after'
          : 'solid-border-after'
        : item.tagName === 'TH'
        ? 'dashed-border-before'
        : 'solid-border-before lg:!tw-border-r-0';

    this.stickyItemClasses = `${baseClasses} ${borderClass}`;

    const classes = this.stickyItemClasses.split(' ');
    classes.forEach((cls) => this.renderer.addClass(item, cls));
  }

  removeStickyStylesOnSmallScreens(stickyItems: NodeListOf<HTMLElement>) {
    if (window.innerWidth < 1024) {
      stickyItems.forEach((item: HTMLElement) => {
        if (item.classList.contains('operations-menu')) {
          const classesToAdd = [
            'tw-z-[1]',
          ];
          this.renderer.removeClass(item, 'lg:!tw-sticky');
          classesToAdd.forEach((cls) => this.renderer.addClass(item, cls));
        } else {
          this.renderer.removeStyle(item, 'left');
          this.renderer.removeStyle(item, 'right');
        }
      });
    }
  }

  removeStickyStyles() {
    const stickyItems = this.el.nativeElement.querySelectorAll('.sticky-item');
    stickyItems.forEach((item: HTMLElement) => {
      this.renderer.removeClass(item, 'lg:!tw-sticky');
      this.renderer.removeStyle(item, 'left');
      this.renderer.removeStyle(item, 'right');
    });
  }
}
